import React from "react";
import ReactDOMServer from "react-dom/server";
import L, { Direction, Map, Marker } from "leaflet";
import { initGeometryUtil } from "../plugins/leaflet/leaflet-geometry-util";
// import { initLeafletSnap } from "../plugins/leaflet/leaflet-snap";
import { Size } from "../../components/Floors/FloorPlan/FloorsPlan";
import { trimText } from "./trimText";
import {
  calculateTopOffset,
  normalizeTooltipScaleFactor,
} from "./calculateTopOffset";
import { ContrastName } from "../../components/shared/ContrastName/ContrastName";

type LeafletIconParams = {
  svgUrl: string;
  size: [number, number];
  anchor?: [number, number];
  type: string;
  className?: string;
};

type DrawMarkerParams = {
  map: Map;
  markers: L.Marker[];
  poiMarkers: L.Marker[];
  layers: L.Layer[];
  handleShapeClick: () => void;
  config: {
    mapSize: Size;
  };
  options: {
    snapEnabled: boolean;
    showTooltips: boolean;
    showRoomMarkers: boolean;
    scaleFactor?: number;
    hasContrastTooltips?: boolean;
  };
  openMarkerId?: string;
};

export const generateTooltipSvg = (text: string, scaleFactor: number = 1) => {
  const normalizedScaleFactor = normalizeTooltipScaleFactor(scaleFactor);
  const baseWidth = 155;
  const baseHeight = 43.75;
  const scaledWidth = baseWidth * normalizedScaleFactor;
  const scaledHeight = baseHeight * normalizedScaleFactor;

  return `<svg width="${scaledWidth}" height="${scaledHeight}" viewBox="0 0 180 50" style="margin-top: ${calculateTopOffset(
    scaleFactor
  )}px">
    <text x="50%" y="40%">
      ${trimText(text, 18)}
    </text>
  </svg>`;
};

export const generateTooltipContent = (
  text: string,
  scaleFactor: number = 1,
  hasContrastTooltips: boolean = false,
  isMarker: boolean = false
) => {
  if (hasContrastTooltips) {
    return ReactDOMServer.renderToString(
      React.createElement(ContrastName, {
        name: trimText(text, 18),
        scaleFactor: scaleFactor,
        isMarker: isMarker,
      })
    );
  }

  return generateTooltipSvg(text, scaleFactor);
};

const distanceThreshold = 60; // Minimum distance to check for other markers
const SHAPE_LABEL_MARGIN_PX = 30;

type ShapeLayer = L.Circle | L.Polygon | L.Rectangle;

const positionShapeTooltip = (
  layer: ShapeLayer,
  alignment: string,
  map: Map
): void => {
  const tooltip = layer.getTooltip();

  if (!tooltip) {
    return;
  }

  const bounds = layer.getBounds();
  const centerPoint = map.latLngToLayerPoint(bounds.getCenter());
  const northPoint = map.latLngToLayerPoint(bounds.getNorthWest());
  const southPoint = map.latLngToLayerPoint(bounds.getSouthWest());

  const targetPoint = centerPoint.clone();
  const boundsHeight = Math.abs(northPoint.y - southPoint.y);
  const proportionalMargin = boundsHeight * 0.2;
  const dynamicMargin = Math.min(SHAPE_LABEL_MARGIN_PX, proportionalMargin);

  if (alignment === "top") {
    targetPoint.y = centerPoint.y - dynamicMargin;
  } else if (alignment === "bottom") {
    targetPoint.y = centerPoint.y + dynamicMargin;
  }

  const targetLatLng = map.layerPointToLatLng(targetPoint);

  tooltip.setLatLng(targetLatLng);
};

export const createLeafletIcon = ({
  svgUrl,
  size,
  anchor,
  type,
  className,
}: LeafletIconParams): L.Icon => {
  return L.icon({
    iconSize: size,
    iconAnchor: anchor || [size[0] / 2, size[1] / 2],
    popupAnchor: [0, -size[1] / 2],
    iconUrl: svgUrl,
    attribution: type,
    shadowSize: [40, 50],
    className: className || undefined,
  });
};

export const getMarkerIndex = (marker: Marker): number => {
  // @ts-ignore
  return marker._index;
};

export const setMarkerIndex = (marker: Marker, index: number): void => {
  // @ts-ignore
  marker._index = index;
};

export const isMarkersOnOneLine = (
  latlngs: [L.LatLngLiteral, L.LatLngLiteral]
) => {
  const lngDifference = Math.abs(latlngs[0].lng - latlngs[1].lng);
  const latDifference = Math.abs(latlngs[0].lat - latlngs[1].lat);

  return lngDifference < 12 || latDifference < 12;
};

export const drawMarkers = ({
  map,
  markers,
  poiMarkers,
  layers,
  handleShapeClick,
  config,
  options,
  openMarkerId,
}: DrawMarkerParams): (() => void) => {
  const cleanupCallbacks: Array<() => void> = [];
  const cleanup = () => {
    [...cleanupCallbacks].reverse().forEach((callback) => callback());
  };

  initGeometryUtil(L);
  // initLeafletSnap(L);

  layers.forEach((layer) => {
    let reposition: (() => void) | undefined;
    let hasBoundTooltip = false;

    //show room's name in center of the shape, if users hide the room markers
    if (!options.showRoomMarkers && options.showTooltips) {
      const attributionParts = layer.options.attribution?.split(
        "-innerGoGetIDSplitter-"
      );

      const layerName = attributionParts?.[1];
      const labelAlignment = attributionParts?.[2] || "center";

      // Leaflet's own `direction`/`offset` tooltip options are left at their
      // defaults here — actual top/bottom/center positioning is applied
      // manually afterwards by positionShapeTooltip(), which repositions the
      // tooltip relative to the shape's bounds based on labelAlignment.
      layer
        .bindTooltip(
          generateTooltipContent(
            layerName || "",
            options.scaleFactor,
            options.hasContrastTooltips,
            false
          ),
          {
            permanent: true,
            direction: "center",
            opacity: 1,
            offset: [0, 0],
            className: `room-permanent-tooltip admin-tooltip layer-tooltip ${
              labelAlignment === "center-rotated" ? "rotated" : ""
            }`,
          }
        )
        .openTooltip();
      hasBoundTooltip = true;

      reposition = () =>
        positionShapeTooltip(layer as ShapeLayer, labelAlignment, map);

      reposition();

      layer.on("edit", reposition);
      // If this layer hasn't been added to the map yet (e.g. it was just
      // recreated after a labelAlignment change), openTooltip() above is a
      // no-op and Leaflet re-opens the permanent tooltip itself once the
      // layer is later added, recentering it and wiping the offset we just
      // set. Re-apply our position whenever the tooltip (re)opens so that
      // late "add" doesn't clobber it.
      layer.on("tooltipopen", reposition);
    }

    layer.off("click");

    const handleLayerClick = () => {
      // handleShapeClick(layer, layer.options.attribution || "");
      handleShapeClick();
    };

    layer.on("click", handleLayerClick);

    cleanupCallbacks.push(() => {
      layer.off("click", handleLayerClick);

      if (reposition) {
        layer.off("edit", reposition);
        layer.off("tooltipopen", reposition);
      }

      if (hasBoundTooltip) {
        layer.unbindTooltip();
      }
    });
  });

  poiMarkers.forEach((poiMarker) => {
    poiMarker.addTo(map);

    cleanupCallbacks.push(() => {
      map.removeLayer(poiMarker);
    });
  });

  markers.forEach((marker, index) => {
    marker.addTo(map);
    let hasBoundTooltip = false;

    setMarkerIndex(marker, index);

    if (openMarkerId && marker.options.attribution === openMarkerId) {
      marker.openPopup();
    }

    if (
      options.showTooltips &&
      marker.options.alt === "Room" &&
      marker.getTooltip() === undefined
    ) {
      let direction: Direction = "bottom";

      const latlng = marker.getLatLng();
      const hasMarkerOnRight = markers.some(
        (otherMarker) =>
          otherMarker !== marker &&
          otherMarker.getLatLng().lng >= latlng.lng &&
          map.distance(latlng, otherMarker.getLatLng()) < distanceThreshold
      );
      const hasMarkerOnLeft = markers.some(
        (otherMarker) =>
          otherMarker !== marker &&
          otherMarker.getLatLng().lng <= latlng.lng &&
          map.distance(latlng, otherMarker.getLatLng()) < distanceThreshold
      );
      const hasMarkerOnTop = markers.some(
        (otherMarker) =>
          otherMarker !== marker &&
          otherMarker.getLatLng().lat >= latlng.lat &&
          map.distance(latlng, otherMarker.getLatLng()) < distanceThreshold
      );
      // const hasMarkerOnBottom = markers.some(
      //   (otherMarker) =>
      //     otherMarker !== marker &&
      //     otherMarker.getLatLng().lat <= latlng.lat &&
      //     map.distance(latlng, otherMarker.getLatLng()) < distanceThreshold
      // );

      if (!hasMarkerOnRight) {
        direction = "bottom";
      } else if (!hasMarkerOnLeft) {
        direction = "left";
      } else if (!hasMarkerOnTop) {
        direction = "top";
      }
      // else if (!hasMarkerOnBottom) {
      //   direction = "bottom";
      //   tooltipOffset = [0, tooltipOffsetBase];
      // }

      marker
        .bindTooltip(
          generateTooltipContent(
            marker.options.title || "",
            options.scaleFactor,
            options.hasContrastTooltips,
            true
          ),
          {
            permanent: true,
            direction: direction,
            opacity: 1,
            className: `room-permanent-tooltip admin-tooltip ${direction} ${options.scaleFactor}`,
          }
        )
        .openTooltip();
      hasBoundTooltip = true;
    }

    cleanupCallbacks.push(() => {
      if (hasBoundTooltip) {
        marker.unbindTooltip();
      }

      map.removeLayer(marker);
    });
  });

  if (!options.snapEnabled) {
    return cleanup;
  }

  const snapGuide = L.polyline([], {
    interactive: false,
    weight: 2,
  }).addTo(map);

  cleanupCallbacks.push(() => {
    map.removeLayer(snapGuide);
  });

  markers.forEach((marker) => {
    // @ts-ignore
    // marker.snapediting = new L.Handler.MarkerSnap(map, marker);
    // @ts-ignore
    // marker.snapediting.addGuideLayer(polyline);
    // @ts-ignore
    // marker.snapediting.enable();
    const handleDragStart = () => {
      // Ensure guide is cleared at start
      snapGuide.setLatLngs([]);
    };

    const handleDrag = (e: L.LeafletEvent) => {
      const currentMarker = e.target as L.Marker;
      const currentLatLng = currentMarker.getLatLng();
      const alignedPositions: L.LatLng[][] = [];

      // Find aligned markers
      markers.forEach((otherMarker) => {
        if (otherMarker === currentMarker) return;

        const otherLatLng = otherMarker.getLatLng();
        const lngDiff = Math.abs(otherLatLng.lng - currentLatLng.lng);
        const latDiff = Math.abs(otherLatLng.lat - currentLatLng.lat);

        // Check alignment within threshold
        if (lngDiff < 0.5) {
          alignedPositions.push([
            new L.LatLng(-config.mapSize.height, otherLatLng.lng),
            new L.LatLng(config.mapSize.height, otherLatLng.lng),
          ]);
        } else if (latDiff < 0.5) {
          alignedPositions.push([
            new L.LatLng(otherLatLng.lat, -config.mapSize.width),
            new L.LatLng(otherLatLng.lat, config.mapSize.width),
          ]);
        }
      });

      // Update guide lines only if we have alignments
      snapGuide.setLatLngs(alignedPositions);
    };

    const handleDragEnd = () => {
      // Clear guide lines immediately when dragging stops
      snapGuide.setLatLngs([]);
    };

    marker.on("dragstart", handleDragStart);
    marker.on("drag", handleDrag);
    marker.on("dragend", handleDragEnd);

    cleanupCallbacks.push(() => {
      marker.off("dragstart", handleDragStart);
      marker.off("drag", handleDrag);
      marker.off("dragend", handleDragEnd);
    });
  });

  return cleanup;
};
